import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

const buildListOrderQuery = (days = 7) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - Math.max(0, days));
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return `
{
  listOrder(
    orderedAt: { gte: "${start.toISOString()}", lt: "${end.toISOString()}" }
    pagination: { page: 1, limit: 50 }
  ) {
    data {
      id
      orderNumber
      totalPrice
      orderedAt
      salesChannel { id name }
      customer { id email firstName lastName }
      orderLineItems {
        id
        quantity
        price
        finalPrice
        variant {
          id
          productId
          name
          sku
          brand { id name }
        }
      }
    }
  }
}
`.trim();
};

export default function IkasApiLab() {
  const [storeName, setStoreName] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [queryText, setQueryText] = useState(buildListOrderQuery());
  const [scriptNotes, setScriptNotes] = useState('// İkas örnek scriptlerini buraya yapıştırabilirsin');
  const [loading, setLoading] = useState(false);
  const [responsePayload, setResponsePayload] = useState(null);
  const [responseMeta, setResponseMeta] = useState(null);
  const [history, setHistory] = useState([]);

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const baseHeaders = useMemo(
    () => ({
      ...(brandId ? { 'X-Brand-ID': brandId } : {}),
    }),
    [brandId]
  );

  const handleSend = async () => {
    try {
      setLoading(true);
      setResponsePayload(null);
      setResponseMeta(null);

      const res = await fetch('/api/labs/ikas/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(brandId ? { 'X-Brand-ID': brandId } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          storeName,
          clientId,
          clientSecret,
          query: queryText
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || data?.details || 'İstek başarısız');
      }
      setResponsePayload(data?.payload ?? data);
      setResponseMeta(data?.meta || { status: res.status, ok: true });
      setHistory(prev => ([
        {
          ts: new Date().toISOString(),
          status: data?.meta?.gqlStatus || res.status,
          ok: true,
          store: storeName.trim(),
          queryPreview: queryText.slice(0, 60).replace(/\s+/g, ' ')
        },
        ...prev
      ]).slice(0, 20));
      toast.success('İstek başarılı');
    } catch (err) {
      console.error('[IkasApiLab] error:', err);
      toast.error(err.message || 'İstek başarısız');
      setResponsePayload({ error: err.message });
      setResponseMeta(null);
    } finally {
      setLoading(false);
    }
  };

  const clearResponse = () => {
    setResponsePayload(null);
    setResponseMeta(null);
  };

  const applyOrderTemplate = () => {
    setQueryText(buildListOrderQuery(7));
    toast.success('Son 7 gün sipariş sorgusu hazırlandı');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Labs · İkas Order Fetch</h1>
        <p className="text-sm text-gray-500 mt-1">
          Store adı + Client ID/Secret gir, script otomatik olarak access token alıp GraphQL isteğini çalıştırsın.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border rounded-xl p-4 space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Store Name</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="petzzshop"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Client ID</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Client Secret</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="text-xs uppercase tracking-wide text-gray-500">Hazır sorgular:</span>
              <button
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border hover:bg-gray-50"
                type="button"
                onClick={applyOrderTemplate}
              >
                listOrder · Son 7 Gün
              </button>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">GraphQL Query</label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 text-xs font-mono"
                rows={14}
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                spellCheck={false}
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${loading ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                onClick={handleSend}
                disabled={loading}
              >
                {loading ? 'Çekiliyor…' : 'Veriyi Çek'}
              </button>
              <button
                className="px-4 py-2 rounded-lg text-sm font-semibold border"
                onClick={clearResponse}
                disabled={loading}
              >
                Yanıtı Temizle
              </button>
            </div>
          </div>

          <div className="bg-white border rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Yanıt</h2>
              {responseMeta && (
                <div className="text-xs text-gray-500">
                  {responseMeta.status} · {responseMeta.elapsed}
                </div>
              )}
            </div>
            {responsePayload ? (
              <pre className="bg-gray-50 rounded-lg p-4 text-xs overflow-x-auto">
                {typeof responsePayload === 'string'
                  ? responsePayload
                  : JSON.stringify(responsePayload, null, 2)}
              </pre>
            ) : (
              <div className="text-sm text-gray-500">Henüz yanıt yok.</div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border rounded-xl p-4">
            <h2 className="text-base font-semibold mb-2">Notlar</h2>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-xs font-mono"
              rows={16}
              value={scriptNotes}
              onChange={(e) => setScriptNotes(e.target.value)}
              spellCheck={false}
            />
          </div>

          <div className="bg-white border rounded-xl p-4">
            <h2 className="text-base font-semibold mb-2">İstek Geçmişi</h2>
            {history.length === 0 ? (
              <p className="text-sm text-gray-500">Henüz kayıt yok.</p>
            ) : (
              <ul className="space-y-2 text-xs font-mono">
                {history.map((h, idx) => (
                  <li key={`${h.ts}-${idx}`} className="border rounded-lg p-2 bg-gray-50">
                    <div className="flex justify-between">
                      <span className="font-semibold">{h.store}.myikas.com</span>
                      <span className={h.ok ? 'text-emerald-600' : 'text-red-600'}>{h.status}</span>
                    </div>
                    <div className="truncate text-gray-600">{h.queryPreview}</div>
                    <div className="text-gray-400">{new Date(h.ts).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

